<template>
  <div class="shipping-rates-page">
    <main-header />

    <section class="shipping-rates container">
      <header class="shipping-header">
        <h1>Shipping Rates</h1>
        <p class="subtitle">
          Live YunExpress shipping options based on destination and weight.
        </p>
      </header>

      <form class="shipping-form" @submit.prevent="onSubmit">
        <div class="form-section">
          <h3 class="section-title">📦 Package Details</h3>
          
          <div class="package-inputs-grid">
            <div class="form-group package-input-group">
              <label for="weight">Weight</label>
              <div class="input-with-unit">
                <input
                  id="weight"
                  v-model.number="weight"
                  type="number"
                  min="0.001"
                  max="10000"
                  step="0.001"
                  class="input input-large"
                  placeholder="0.5"
                  required
                >
                <span class="unit-label">kg</span>
              </div>
              <small class="input-hint">Range: 0.001 - 10,000 kg</small>
            </div>

            <div class="form-group package-input-group">
              <label for="length">Length</label>
              <div class="input-with-unit">
                <input
                  id="length"
                  v-model.number="length"
                  type="number"
                  min="0.1"
                  max="10000"
                  step="0.1"
                  class="input input-large"
                  placeholder="10"
                  required
                >
                <span class="unit-label">cm</span>
              </div>
              <small class="input-hint">Package length</small>
            </div>

            <div class="form-group package-input-group">
              <label for="width">Width</label>
              <div class="input-with-unit">
                <input
                  id="width"
                  v-model.number="width"
                  type="number"
                  min="0.1"
                  max="10000"
                  step="0.1"
                  class="input input-large"
                  placeholder="10"
                  required
                >
                <span class="unit-label">cm</span>
              </div>
              <small class="input-hint">Package width</small>
            </div>

            <div class="form-group package-input-group">
              <label for="height">Height</label>
              <div class="input-with-unit">
                <input
                  id="height"
                  v-model.number="height"
                  type="number"
                  min="0.1"
                  max="10000"
                  step="0.1"
                  class="input input-large"
                  placeholder="10"
                  required
                >
                <span class="unit-label">cm</span>
              </div>
              <small class="input-hint">Package height</small>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title">🌍 Shipping Route</h3>
          
          <div class="form-row">
            <div class="form-group">
              <label for="origin">Origin Warehouse</label>
              <select
                id="origin"
                v-model="origin"
                class="select"
                required
              >
                <option value="">Select origin warehouse...</option>
                <option value="YT-HK">🇭🇰 Hong Kong - YT-HK</option>
                <option value="YT-SZ">🇨🇳 Shenzhen, China - YT-SZ</option>
                <option value="YT-GZ">🇨🇳 Guangzhou, China - YT-GZ</option>
                <option value="YT-YW">🇨🇳 Yiwu, China - YT-YW</option>
              </select>
            </div>

            <div class="form-group">
              <label for="country">Destination Country</label>
              <input
                id="country"
                v-model="countrySearch"
                type="text"
                class="input"
                list="country-list"
                placeholder="Type to search countries..."
                @input="onCountryInput"
                @blur="validateCountry"
                required
              >
              <datalist id="country-list">
                <option
                  v-for="c in countries"
                  :key="c.code"
                  :value="c.name"
                >
                  {{ c.code }} - {{ c.name }}
                </option>
              </datalist>
              <small v-if="country" class="country-code-hint">Selected: {{ country }}</small>
            </div>

            <div class="form-group">
              <label for="zipCode">Zip / Postal Code</label>
              <input
                id="zipCode"
                v-model="zipCode"
                type="text"
                class="input"
                placeholder="e.g. 10001"
                required
              >
            </div>
          </div>
        </div>

        <div class="actions">
          <button type="submit" class="btn primary" :disabled="loading">
            <span v-if="!loading">Search Shipping Rates</span>
            <span v-else class="loading-inline">
              <span class="spinner" />
              Loading...
            </span>
          </button>
        </div>
      </form>

      <div v-if="error" class="status status-error">
        <strong>Error:</strong>
        <span>{{ error }}</span>
      </div>

      <div v-if="loading" class="status status-loading">
        <span class="spinner" />
        Fetching live rates from YunExpress...
      </div>

      <section v-if="hasResult" class="rates-section">
        <div class="rate-card filters-card">
          <div class="filters-header">
            <h2>Available Shipping Options</h2>
            <span class="results-count">{{ filteredRates.length }} of {{ normalizedRates.length }} options</span>
            <span v-if="origin" class="origin-badge">📦 From: {{ getOriginName(origin) }}</span>
          </div>

          <div class="filters-section">
            <div class="filter-group">
              <label class="filter-label">⚡ Delivery Time:</label>
              <div class="filter-buttons">
                <button
                  type="button"
                  class="btn filter-btn"
                  :class="{ active: deliveryFilter === 'all' }"
                  @click="setDeliveryFilter('all')"
                >
                  All speeds
                </button>
                <button
                  type="button"
                  class="btn filter-btn"
                  :class="{ active: deliveryFilter === '7' }"
                  @click="setDeliveryFilter('7')"
                >
                  Under 7 days
                </button>
                <button
                  type="button"
                  class="btn filter-btn"
                  :class="{ active: deliveryFilter === '10' }"
                  @click="setDeliveryFilter('10')"
                >
                  Under 10 days
                </button>
                <button
                  type="button"
                  class="btn filter-btn"
                  :class="{ active: deliveryFilter === '15' }"
                  @click="setDeliveryFilter('15')"
                >
                  Under 15 days
                </button>
                <button
                  type="button"
                  class="btn filter-btn"
                  :class="{ active: deliveryFilter === '30' }"
                  @click="setDeliveryFilter('30')"
                >
                  Under 30 days
                </button>
              </div>
            </div>

            <div class="filter-group">
              <label class="filter-label">📊 Sort by:</label>
              <div class="filter-buttons">
                <button
                  type="button"
                  class="btn filter-btn"
                  :class="{ active: sortMode === 'best' }"
                  @click="setSort('best')"
                >
                  ⭐ Best value
                </button>
                <button
                  type="button"
                  class="btn filter-btn"
                  :class="{ active: sortMode === 'fastest' }"
                  @click="setSort('fastest')"
                >
                  🚀 Fastest
                </button>
                <button
                  type="button"
                  class="btn filter-btn"
                  :class="{ active: sortMode === 'cheapest' }"
                  @click="setSort('cheapest')"
                >
                  💰 Cheapest
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="rates-list">
          <article
            v-for="(entry, idx) in sortedRates"
            :key="entry.index"
            class="rate-card rate-option"
            :class="{ 'best-option': idx === 0 && sortMode === 'best' }"
          >
            <div class="rate-header">
              <div class="rate-title-section">
                <h3 class="rate-title">
                  {{ entry.item.product_name || entry.item.productName || entry.item.price_name || entry.item.product_code }}
                </h3>
                <div class="rate-meta">
                  <span class="rate-code">Code: {{ entry.item.product_code || 'n/a' }}</span>
                  <span v-if="entry.item.fee_name" class="rate-fee">Fee: {{ entry.item.fee_name }}</span>
                </div>
              </div>
              <div class="rate-badges">
                <span v-if="idx === 0 && sortMode === 'best'" class="badge badge-best">⭐ Best Value</span>
                <span v-if="idx === 0 && sortMode === 'fastest'" class="badge badge-fast">⚡ Fastest</span>
                <span v-if="idx === 0 && sortMode === 'cheapest'" class="badge badge-cheap">💰 Cheapest</span>
              </div>
            </div>

            <div class="rate-main">
              <div class="rate-price-section">
                <div class="price-label">Total Price</div>
                <div class="price-value">
                  <template v-if="Number.isFinite(entry.price)">
                    <span class="currency">{{ entry.item.convert_currency || entry.item.currency || 'USD' }}</span>
                    <span class="amount">{{ entry.price.toFixed(2) }}</span>
                  </template>
                  <template v-else>
                    <span class="amount">n/a</span>
                  </template>
                </div>
              </div>

              <div class="rate-delivery-section">
                <div class="delivery-icon">📅</div>
                <div class="delivery-info">
                  <div class="delivery-label">Estimated Delivery</div>
                  <div class="delivery-days">{{ entry.daysLabel }} days</div>
                </div>
              </div>
            </div>

            <div class="rate-details-grid">
              <div class="detail-item">
                <span class="detail-icon">🏷️</span>
                <div class="detail-content">
                  <span class="detail-label">Service Type</span>
                  <strong class="detail-value">{{ entry.item.fee_name || entry.item.price_name || 'Standard' }}</strong>
                </div>
              </div>
              <div v-if="entry.item.price_type" class="detail-item">
                <span class="detail-icon">💳</span>
                <div class="detail-content">
                  <span class="detail-label">Price Type</span>
                  <strong class="detail-value">{{ entry.item.price_type }}</strong>
                </div>
              </div>
              <div v-if="entry.item.price_name" class="detail-item">
                <span class="detail-icon">📋</span>
                <div class="detail-content">
                  <span class="detail-label">Price Plan</span>
                  <strong class="detail-value">{{ entry.item.price_name }}</strong>
                </div>
              </div>
              <div v-if="entry.valueScore !== Number.POSITIVE_INFINITY" class="detail-item">
                <span class="detail-icon">⭐</span>
                <div class="detail-content">
                  <span class="detail-label">Value Score</span>
                  <strong class="detail-value">{{ entry.valueScore.toFixed(2) }} days/$</strong>
                </div>
              </div>
            </div>
          </article>
        </div>

        <section class="debug-section">
          <details>
            <summary>Show raw YunExpress response (debug)</summary>
            <pre>{{ JSON.stringify(rawResponse, null, 2) }}</pre>
          </details>
        </section>
      </section>

      <section v-else-if="!loading && !error" class="empty-state">
        <p>Fill in the form above and click "Search Shipping Rates" to see options.</p>
      </section>
    </section>

    <main-footer />
  </div>
</template>

<script>
import MainHeader from "@/components/MainHeader.vue";
import MainFooter from "@/components/MainFooter.vue";
import { getShippingRates } from "@/services/firebaseApi";

export default {
  name: "ShippingRatesView",
  components: {
    MainHeader,
    MainFooter,
  },
  data() {
    return {
      country: "",
      countrySearch: "",
      zipCode: "",
      weight: 0.5,
      length: 1,
      width: 1,
      height: 1,
      origin: "",
      loading: false,
      error: null,
      rawResponse: null,
      sortMode: "best",
      deliveryFilter: "all",
    };
  },
  computed: {
    countries() {
      return [
        { code: "AD", name: "Andorra" },
        { code: "AE", name: "United Arab Emirates" },
        { code: "AF", name: "Afghanistan" },
        { code: "AG", name: "Antigua & Barbuda" },
        { code: "AI", name: "Anguilla" },
        { code: "AL", name: "Albania" },
        { code: "AM", name: "Armenia" },
        { code: "AN", name: "Netherlands Antilles" },
        { code: "AO", name: "Angola" },
        { code: "AQ", name: "Antarctica" },
        { code: "AR", name: "Argentina" },
        { code: "AS", name: "American Samoa" },
        { code: "AT", name: "Austria" },
        { code: "AU", name: "Australia" },
        { code: "AW", name: "Aruba" },
        { code: "AX", name: "Åland Islands" },
        { code: "AZ", name: "Azerbaijan" },
        { code: "BA", name: "Bosnia & Herzegovina" },
        { code: "BB", name: "Barbados" },
        { code: "BD", name: "Bangladesh" },
        { code: "BE", name: "Belgium" },
        { code: "BF", name: "Burkina Faso" },
        { code: "BG", name: "Bulgaria" },
        { code: "BH", name: "Bahrain" },
        { code: "BI", name: "Burundi" },
        { code: "BJ", name: "Benin" },
        { code: "BL", name: "Saint Barthélemy" },
        { code: "BM", name: "Bermuda" },
        { code: "BN", name: "Brunei" },
        { code: "BO", name: "Bolivia" },
        { code: "BQ", name: "Caribbean Netherlands" },
        { code: "BR", name: "Brazil" },
        { code: "BS", name: "The Bahamas" },
        { code: "BT", name: "Bhutan" },
        { code: "BV", name: "Bouvet Island" },
        { code: "BW", name: "Botswana" },
        { code: "BY", name: "Belarus" },
        { code: "BZ", name: "Belize" },
        { code: "CA", name: "Canada" },
        { code: "CC", name: "Cocos (Keeling) Islands" },
        { code: "CD", name: "Democratic Republic of the Congo" },
        { code: "CF", name: "Central African Republic" },
        { code: "CG", name: "Republic of the Congo" },
        { code: "CH", name: "Switzerland" },
        { code: "CI", name: "Côte d'Ivoire" },
        { code: "CK", name: "Cook Islands" },
        { code: "CL", name: "Chile" },
        { code: "CM", name: "Cameroon" },
        { code: "CN", name: "China" },
        { code: "CO", name: "Colombia" },
        { code: "CR", name: "Costa Rica" },
        { code: "CU", name: "Cuba" },
        { code: "CV", name: "Cape Verde" },
        { code: "CW", name: "Curaçao" },
        { code: "CX", name: "Christmas Island" },
        { code: "CY", name: "Cyprus" },
        { code: "CZ", name: "Czech Republic" },
        { code: "DE", name: "Germany" },
        { code: "DJ", name: "Djibouti" },
        { code: "DK", name: "Denmark" },
        { code: "DM", name: "Dominica" },
        { code: "DO", name: "Dominican Republic" },
        { code: "DZ", name: "Algeria" },
        { code: "EC", name: "Ecuador" },
        { code: "EE", name: "Estonia" },
        { code: "EG", name: "Egypt" },
        { code: "EH", name: "Western Sahara" },
        { code: "ER", name: "Eritrea" },
        { code: "ES", name: "Spain" },
        { code: "ET", name: "Ethiopia" },
        { code: "FI", name: "Finland" },
        { code: "FJ", name: "Fiji" },
        { code: "FK", name: "Falkland Islands" },
        { code: "FM", name: "Federated States of Micronesia" },
        { code: "FO", name: "Faroe Islands" },
        { code: "FR", name: "France" },
        { code: "GA", name: "Gabon" },
        { code: "GB", name: "United Kingdom" },
        { code: "GD", name: "Grenada" },
        { code: "GE", name: "Georgia" },
        { code: "GF", name: "French Guiana" },
        { code: "GG", name: "Guernsey" },
        { code: "GH", name: "Ghana" },
        { code: "GI", name: "Gibraltar" },
        { code: "GL", name: "Greenland" },
        { code: "GM", name: "Gambia" },
        { code: "GN", name: "Guinea" },
        { code: "GP", name: "Guadeloupe" },
        { code: "GQ", name: "Equatorial Guinea" },
        { code: "GR", name: "Greece" },
        { code: "GS", name: "South Georgia and the South Sandwich Islands" },
        { code: "GT", name: "Guatemala" },
        { code: "GU", name: "Guam" },
        { code: "GW", name: "Guinea-Bissau" },
        { code: "GY", name: "Guyana" },
        { code: "HK", name: "Hong Kong" },
        { code: "HM", name: "Heard Island and McDonald Islands" },
        { code: "HN", name: "Honduras" },
        { code: "HR", name: "Croatia" },
        { code: "HT", name: "Haiti" },
        { code: "HU", name: "Hungary" },
        { code: "ID", name: "Indonesia" },
        { code: "IE", name: "Ireland" },
        { code: "IL", name: "Israel" },
        { code: "IM", name: "Isle of Man" },
        { code: "IN", name: "India" },
        { code: "IO", name: "British Indian Ocean Territory" },
        { code: "IQ", name: "Iraq" },
        { code: "IR", name: "Iran" },
        { code: "IS", name: "Iceland" },
        { code: "IT", name: "Italy" },
        { code: "JE", name: "Jersey" },
        { code: "JM", name: "Jamaica" },
        { code: "JO", name: "Jordan" },
        { code: "JP", name: "Japan" },
        { code: "KE", name: "Kenya" },
        { code: "KG", name: "Kyrgyzstan" },
        { code: "KH", name: "Cambodia" },
        { code: "KI", name: "Kiribati" },
        { code: "KM", name: "The Comoros" },
        { code: "KN", name: "Saint Kitts and Nevis" },
        { code: "KP", name: "North Korea" },
        { code: "KR", name: "South Korea" },
        { code: "KW", name: "Kuwait" },
        { code: "KY", name: "Cayman Islands" },
        { code: "KZ", name: "Kazakhstan" },
        { code: "LA", name: "Laos" },
        { code: "LB", name: "Lebanon" },
        { code: "LC", name: "Saint Lucia" },
        { code: "LI", name: "Liechtenstein" },
        { code: "LK", name: "Sri Lanka" },
        { code: "LR", name: "Liberia" },
        { code: "LS", name: "Lesotho" },
        { code: "LT", name: "Lithuania" },
        { code: "LU", name: "Luxembourg" },
        { code: "LV", name: "Latvia" },
        { code: "LY", name: "Libya" },
        { code: "MA", name: "Morocco" },
        { code: "MC", name: "Monaco" },
        { code: "MD", name: "Moldova" },
        { code: "ME", name: "Montenegro" },
        { code: "MF", name: "Saint Martin (France)" },
        { code: "MG", name: "Madagascar" },
        { code: "MH", name: "Marshall Islands" },
        { code: "MK", name: "Republic of Macedonia (FYROM)" },
        { code: "ML", name: "Mali" },
        { code: "MM", name: "Myanmar (Burma)" },
        { code: "MN", name: "Mongolia" },
        { code: "MO", name: "Macao" },
        { code: "MP", name: "Northern Mariana Islands" },
        { code: "MQ", name: "Martinique" },
        { code: "MR", name: "Mauritania" },
        { code: "MS", name: "Montserrat" },
        { code: "MT", name: "Malta" },
        { code: "MU", name: "Mauritius" },
        { code: "MV", name: "Maldives" },
        { code: "MW", name: "Malawi" },
        { code: "MX", name: "Mexico" },
        { code: "MY", name: "Malaysia" },
        { code: "MZ", name: "Mozambique" },
        { code: "NA", name: "Namibia" },
        { code: "NC", name: "New Caledonia" },
        { code: "NE", name: "Niger" },
        { code: "NF", name: "Norfolk Island" },
        { code: "NG", name: "Nigeria" },
        { code: "NI", name: "Nicaragua" },
        { code: "NL", name: "Netherlands" },
        { code: "NO", name: "Norway" },
        { code: "NP", name: "Nepal" },
        { code: "NR", name: "Nauru" },
        { code: "NU", name: "Niue" },
        { code: "NZ", name: "New Zealand" },
        { code: "OM", name: "Oman" },
        { code: "PA", name: "Panama" },
        { code: "PE", name: "Peru" },
        { code: "PF", name: "French Polynesia" },
        { code: "PG", name: "Papua New Guinea" },
        { code: "PH", name: "Philippines" },
        { code: "PK", name: "Pakistan" },
        { code: "PL", name: "Poland" },
        { code: "PM", name: "Saint Pierre and Miquelon" },
        { code: "PN", name: "Pitcairn Islands" },
        { code: "PR", name: "Puerto Rico" },
        { code: "PS", name: "Palestinian Territories" },
        { code: "PT", name: "Portugal" },
        { code: "PW", name: "Palau" },
        { code: "PY", name: "Paraguay" },
        { code: "QA", name: "Qatar" },
        { code: "RE", name: "Réunion" },
        { code: "RO", name: "Romania" },
        { code: "RS", name: "Serbia" },
        { code: "RU", name: "Russian Federation" },
        { code: "RW", name: "Rwanda" },
        { code: "SA", name: "Saudi Arabia" },
        { code: "SB", name: "Solomon Islands" },
        { code: "SC", name: "Seychelles" },
        { code: "SD", name: "Sudan" },
        { code: "SE", name: "Sweden" },
        { code: "SG", name: "Singapore" },
        { code: "SH", name: "Saint Helena" },
        { code: "SI", name: "Slovenia" },
        { code: "SJ", name: "Svalbard and Jan Mayen" },
        { code: "SK", name: "Slovakia" },
        { code: "SL", name: "Sierra Leone" },
        { code: "SM", name: "San Marino" },
        { code: "SN", name: "Senegal" },
        { code: "SO", name: "Somalia" },
        { code: "SR", name: "Suriname" },
        { code: "SS", name: "South Sudan" },
        { code: "ST", name: "Sao Tome & Principe" },
        { code: "SV", name: "El Salvador" },
        { code: "SX", name: "Sint Maarten" },
        { code: "SY", name: "Syria" },
        { code: "SZ", name: "Swaziland" },
        { code: "TC", name: "Turks & Caicos Islands" },
        { code: "TD", name: "Chad" },
        { code: "TF", name: "French Southern Territories" },
        { code: "TG", name: "Togo" },
        { code: "TH", name: "Thailand" },
        { code: "TJ", name: "Tajikistan" },
        { code: "TK", name: "Tokelau" },
        { code: "TL", name: "Timor-Leste (East Timor)" },
        { code: "TM", name: "Turkmenistan" },
        { code: "TN", name: "Tunisia" },
        { code: "TO", name: "Tonga" },
        { code: "TR", name: "Turkey" },
        { code: "TT", name: "Trinidad and Tobago" },
        { code: "TV", name: "Tuvalu" },
        { code: "TW", name: "Taiwan" },
        { code: "TZ", name: "Tanzania" },
        { code: "UA", name: "Ukraine" },
        { code: "UG", name: "Uganda" },
        { code: "UM", name: "United States Minor Outlying Islands" },
        { code: "US", name: "United States of America" },
        { code: "UY", name: "Uruguay" },
        { code: "UZ", name: "Uzbekistan" },
        { code: "VA", name: "Vatican City" },
        { code: "VC", name: "Saint Vincent and the Grenadines" },
        { code: "VE", name: "Venezuela" },
        { code: "VG", name: "British Virgin Islands" },
        { code: "VI", name: "United States Virgin Islands" },
        { code: "VN", name: "Vietnam" },
        { code: "VU", name: "Vanuatu" },
        { code: "WF", name: "Wallis and Futuna" },
        { code: "WS", name: "Samoa" },
        { code: "YE", name: "Yemen" },
        { code: "YT", name: "Mayotte" },
        { code: "ZA", name: "South Africa" },
        { code: "ZM", name: "Zambia" },
        { code: "ZW", name: "Zimbabwe" },
      ];
    },
    hasResult() {
      return this.rawResponse &&
        Array.isArray(this.rawResponse.result) &&
        this.rawResponse.result.length > 0;
    },
    normalizedRates() {
      if (!this.hasResult) {
        return [];
      }

      const results = this.rawResponse.result;

      return results.map((item, index) => {
        let priceRaw = item.convert_amount;
        if (priceRaw === undefined || priceRaw === null) {
          priceRaw = item.calculate_amount;
        }
        if (priceRaw === undefined || priceRaw === null) {
          priceRaw = item.freight;
        }
        if (priceRaw === undefined || priceRaw === null) {
          priceRaw = item.price;
        }

        const price = typeof priceRaw === "number" ?
          priceRaw : parseFloat(priceRaw);

        let minDays = null;
        let maxDays = null;
        let avgDays = null;
        if (item.interval_day && typeof item.interval_day === "string") {
          const parts = item.interval_day.split("-")
            .map((p) => parseInt(p.trim(), 10))
            .filter((n) => !Number.isNaN(n));
          if (parts.length === 2) {
            [minDays, maxDays] = parts;
            avgDays = (parts[0] + parts[1]) / 2;
          } else if (parts.length === 1) {
            [minDays] = parts;
            maxDays = parts[0];
            avgDays = parts[0];
          }
        }
        if (avgDays === null) {
          avgDays = 9999;
        }

        const valueScore = (price && avgDays) ?
          (avgDays / price) : Number.POSITIVE_INFINITY;

        const daysLabel = item.interval_day ||
          (minDays && maxDays ? `${minDays}-${maxDays}` : "n/a");

        return {
          item,
          index,
          price,
          minDays,
          maxDays,
          avgDays,
          valueScore,
          daysLabel,
        };
      });
    },
    filteredRates() {
      let items = this.normalizedRates.slice();

      // Apply delivery time filter
      if (this.deliveryFilter !== 'all') {
        const maxDays = parseInt(this.deliveryFilter, 10);
        items = items.filter((entry) => {
          // Use maxDays from the entry if available, otherwise avgDays
          const deliveryDays = entry.maxDays !== null ? entry.maxDays : entry.avgDays;
          return deliveryDays <= maxDays;
        });
      }

      return items;
    },
    sortedRates() {
      const items = this.filteredRates.slice();
      if (!items.length) {
        return items;
      }

      if (this.sortMode === "fastest") {
        items.sort((a, b) => {
          if (a.avgDays === b.avgDays) {
            return (a.price || 0) - (b.price || 0);
          }
          return a.avgDays - b.avgDays;
        });
      } else if (this.sortMode === "cheapest") {
        items.sort((a, b) => {
          if ((a.price || 0) === (b.price || 0)) {
            return a.avgDays - b.avgDays;
          }
          return (a.price || 0) - (b.price || 0);
        });
      } else {
        // best value: lowest days/price ratio
        items.sort((a, b) => a.valueScore - b.valueScore);
      }

      return items;
    },
  },
  methods: {
    onCountryInput() {
      // Try to find matching country as user types
      const match = this.countries.find(
        (c) => c.name.toLowerCase() === this.countrySearch.toLowerCase()
      );
      if (match) {
        this.country = match.code;
      } else {
        this.country = "";
      }
    },
    validateCountry() {
      // On blur, validate and set the country code
      const match = this.countries.find(
        (c) => c.name.toLowerCase() === this.countrySearch.toLowerCase()
      );
      if (match) {
        this.country = match.code;
        this.countrySearch = match.name;
      } else if (this.countrySearch) {
        // Try partial match
        const partial = this.countries.find(
          (c) => c.name.toLowerCase().includes(this.countrySearch.toLowerCase())
        );
        if (partial) {
          this.country = partial.code;
          this.countrySearch = partial.name;
        }
      }
    },
    async onSubmit() {
      this.error = null;
      this.rawResponse = null;
      this.loading = true;

      try {
        const data = await getShippingRates({
          country: this.country,
          zipCode: this.zipCode,
          weight: this.weight,
          length: this.length,
          width: this.width,
          height: this.height,
          origin: this.origin || undefined,
        });
        this.rawResponse = data;
      } catch (e) {
        this.error = e.message || "Failed to fetch shipping rates";
      } finally {
        this.loading = false;
      }
    },
    setSort(mode) {
      this.sortMode = mode;
    },
    setDeliveryFilter(filter) {
      this.deliveryFilter = filter;
    },
    getOriginName(originCode) {
      const origins = {
        "YT-HK": "Hong Kong",
        "YT-SZ": "Shenzhen",
        "YT-GZ": "Guangzhou",
        "YT-YW": "Yiwu",
      };
      return origins[originCode] || originCode;
    },
  },
};
</script>

<style scoped>
.shipping-rates-page {
  background-color: #f8fafc;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.shipping-rates {
  max-width: 960px;
  margin: 40px auto;
  padding: 20px;
}

.shipping-header {
  margin-bottom: 24px;
}

.shipping-header h1 {
  font-size: 28px;
  margin-bottom: 8px;
}

.subtitle {
  color: #64748b;
  font-size: 14px;
}

.shipping-form {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 10px 40px rgba(15, 23, 42, 0.1), 0 2px 8px rgba(15, 23, 42, 0.05);
  margin-bottom: 24px;
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.form-section {
  margin-bottom: 24px;
}

.form-section:last-child {
  margin-bottom: 0;
}

.section-title {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e2e8f0;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}

.package-inputs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
}

.package-input-group {
  position: relative;
}

.input-with-unit {
  position: relative;
  display: flex;
  align-items: center;
}

.input-large {
  font-size: 18px;
  padding: 14px 60px 14px 16px;
  font-weight: 600;
  color: #0f172a;
  width: 100%;
  min-width: 0;
}

.unit-label {
  position: absolute;
  right: 12px;
  font-size: 13px;
  font-weight: 700;
  color: #64748b;
  background: #f1f5f9;
  padding: 5px 12px;
  border-radius: 6px;
  pointer-events: none;
  user-select: none;
}

.input-hint {
  display: block;
  margin-top: 6px;
  font-size: 11px;
  color: #94a3b8;
  font-style: italic;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 6px;
}

.input,
.select {
  border-radius: 10px;
  border: 2px solid #e2e8f0;
  padding: 12px 16px;
  font-size: 15px;
  outline: none;
  background-color: #ffffff;
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;
}

.input:focus,
.select:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  background-color: #fefefe;
}

.select {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 12 12'%3E%3Cpath fill='%23475569' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 40px;
  font-weight: 500;
}

.actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-start;
}

.btn {
  border-radius: 999px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  padding: 8px 16px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn.primary {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: #f9fafb;
  font-weight: 600;
  padding: 12px 24px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.2);
  transition: all 0.2s ease;
}

.btn.primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.3);
}

.btn.primary:disabled {
  opacity: 0.6;
  cursor: wait;
}

.btn.secondary {
  background: #f1f5f9;
  color: #334155;
  font-weight: 600;
  transition: all 0.2s ease;
}

.btn.secondary:hover {
  background: #e2e8f0;
}

.btn.secondary.active {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: #f9fafb;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.2);
}

.loading-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.spinner {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  border: 2px solid #cbd5f5;
  border-top-color: #111827;
  animation: spin 0.8s linear infinite;
}

.status {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
}

.status-loading {
  background: #eff6ff;
  color: #1d4ed8;
}

.status-error {
  background: #fee2e2;
  color: #b91c1c;
}

.rates-section {
  margin-top: 20px;
}

.rate-card {
  background-color: #ffffff;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.08);
  margin-bottom: 16px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.rate-card:hover {
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.12);
  transform: translateY(-2px);
}

.rate-option {
  position: relative;
  overflow: hidden;
}

.rate-option.best-option {
  border: 2px solid #fbbf24;
  background: linear-gradient(135deg, #fffbeb 0%, #ffffff 100%);
}

.rate-option.best-option::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #fbbf24 0%, #f59e0b 100%);
}

.filters-card {
  margin-bottom: 18px;
}

.filters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 8px;
}

.filters-header h2 {
  margin: 0;
  font-size: 20px;
}

.results-count {
  font-size: 14px;
  color: #64748b;
  background: #f1f5f9;
  padding: 4px 12px;
  border-radius: 999px;
  font-weight: 600;
}

.origin-badge {
  font-size: 13px;
  color: #ffffff;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  padding: 6px 14px;
  border-radius: 999px;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.filters-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  font-size: 13px;
  font-weight: 700;
  color: #334155;
  display: flex;
  align-items: center;
  gap: 4px;
}

.filter-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.btn.filter-btn {
  background: #ffffff;
  color: #475569;
  border: 2px solid #e2e8f0;
  font-weight: 600;
  font-size: 13px;
  padding: 8px 14px;
  transition: all 0.2s ease;
}

.btn.filter-btn:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.btn.filter-btn.active {
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  color: #ffffff;
  border-color: #6366f1;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.rate-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 8px;
}

.rate-title-section {
  flex: 1;
}

.rate-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px 0;
}

.rate-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
}

.rate-code,
.rate-fee {
  font-size: 11px;
  color: #64748b;
  background: #f1f5f9;
  padding: 3px 10px;
  border-radius: 999px;
  font-weight: 600;
}

.rate-fee {
  background: #e0e7ff;
  color: #4f46e5;
}

.rate-badges {
  display: flex;
  gap: 6px;
}

.badge {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
}

.badge-best {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #92400e;
  border: 1px solid #fbbf24;
}

.badge-fast {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
  border: 1px solid #60a5fa;
}

.badge-cheap {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #065f46;
  border: 1px solid #34d399;
}

.rate-main {
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
  padding: 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 10px;
  flex-wrap: wrap;
}

.rate-price-section {
  flex: 1;
  min-width: 140px;
}

.price-label {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
  font-weight: 500;
}

.price-value {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.currency {
  font-size: 14px;
  font-weight: 600;
  color: #475569;
}

.amount {
  font-size: 28px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1;
}

.rate-delivery-section {
  display: flex;
  gap: 10px;
  align-items: center;
  flex: 1;
  min-width: 160px;
}

.delivery-icon {
  font-size: 32px;
}

.delivery-info {
  display: flex;
  flex-direction: column;
}

.delivery-label {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 2px;
}

.delivery-days {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.rate-details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
}

.detail-item {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 10px;
  background: #f8fafc;
  border-radius: 8px;
}

.detail-icon {
  font-size: 18px;
  line-height: 1;
}

.detail-content {
  flex: 1;
  min-width: 0;
}

.detail-label {
  display: block;
  font-size: 11px;
  color: #64748b;
  margin-bottom: 2px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.detail-value {
  font-size: 14px;
  color: #0f172a;
  font-weight: 600;
}

.debug-section {
  margin-top: 12px;
}

.debug-section summary {
  cursor: pointer;
  font-size: 13px;
  color: #4b5563;
}

.debug-section pre {
  margin-top: 8px;
  background: #0f172a;
  color: #e5e7eb;
  padding: 10px;
  border-radius: 8px;
  font-size: 11px;
  max-height: 260px;
  overflow: auto;
}

.empty-state {
  margin-top: 24px;
  font-size: 14px;
  color: #6b7280;
}

.country-code-hint {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #6366f1;
  font-weight: 600;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .shipping-rates {
    margin: 20px auto;
    padding: 12px;
  }

  .shipping-form {
    padding: 20px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .package-inputs-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .input-large {
    font-size: 16px;
  }

  .rate-main {
    flex-direction: column;
    gap: 16px;
  }

  .rate-header {
    flex-direction: column;
  }

  .rate-badges {
    align-self: flex-start;
  }

  .amount {
    font-size: 24px;
  }

  .filters-section {
    gap: 12px;
  }

  .filter-buttons {
    gap: 6px;
  }

  .btn.filter-btn {
    font-size: 12px;
    padding: 6px 10px;
  }
}
</style>
